import { Component, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Company } from "@models/companies/company";
import { CompanyRecord } from "@models/companies/company-record";
import { Document } from "@models/documents/document";
import { DocumentationType, } from "@models/documents/document-enums";
import { EntityChangeData } from "@models/documents/entity-change-data";
import { IndividualData } from "@models/individuals/individual-data";
import { CompanyChangeOfficerType } from "@models/relationships/company-change-officer-type";
import { Relationship } from "@models/relationships/relationship";
import { RelationshipType, RelationshipTypeLabels } from "@models/relationships/relationship-type.enum";
import { SelectOption } from "@models/ui-models/select-options";
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { CheckboxGroupComponent } from "@shared/controls/checkbox-group/checkbox-group.component";
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import { RadioComponent } from "@shared/controls/radio/radio.component";
import { SelectComponent } from "@shared/controls/select/select.component";
import { TextareaComponent } from "@shared/controls/textarea/textarea.component";
import { ACNPipe } from "@shared/pipes/acnPipe";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { DatePickerFormValidators, DatepickerHelper } from "@shared/validators/date-picker-form-validators";
import { combineLatest, forkJoin, startWith } from "rxjs";
import {
  CompanyChangeOfficer,
  ReasonOfCessation,
  reasonOfCessationOptions
} from "../../../models/company/company-change-officer.model";
import { getForm484LateFeeNotification } from "../../../utils/get-late-fee-notification";
import {
  BaseBulkStepperFormComponent,
  StepperFormComponent,
  StepperFormDescriptionComponent
} from "../../stepper-form";
import { IBulkAppliedChange } from "../base-bulk-stepper-form/base-bulk-stepper-form.component";
import { BulkCeaseCompanyOfficeholderStepsEnum } from "./bulk-company-change-officer-cease-steps.enum";

@Component({
  selector: 'app-bulk-company-change-officer-cease',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    StepperFormComponent,
    StepperFormDescriptionComponent,
    DatePickerComponent,
    SelectComponent,
    CheckboxGroupComponent,
    TextareaComponent,
    RadioComponent,
    DisclaimerComponent,
    ListGridClientSideComponent
  ],
  providers: [],
  templateUrl: './bulk-company-change-officer-cease.component.html',
  styleUrls: ['../../stepper-form/base-stepper-component/base-stepper-form.component.scss']
})
export class BulkCompanyChangeOfficerCeaseComponent extends BaseBulkStepperFormComponent<BulkCeaseCompanyOfficeholderStepsEnum, CompanyChangeOfficer> implements OnInit {
  @Input() officers: Record<string, Relationship[]> = {};
  @Input() officersSelectOptions: SelectOption[] = [];
  @Input() companyRecords: CompanyRecord[] = [];

  readonly customDatepickerErrors = DatePickerFormValidators.errorMessagesWithDateOfEstablishmentErrorMessages;
  readonly reasonOfCessationOptions = reasonOfCessationOptions;
  override readonly StepsEnum = BulkCeaseCompanyOfficeholderStepsEnum;

  minDate!: NgbDateStruct;
  maxDate!: NgbDateStruct;

  commonOfficeholdersSelectOptions: SelectOption<string>[] = [];
  commonOfficeholdersAvailablePositions: Record<string, RelationshipType[]> = {};
  commonOfficeholdersAvailablePositionsOptions: SelectOption<RelationshipType>[] = [];

  form = new FormGroup({
    changeDate: new FormControl<Date | null>(DatepickerHelper.getToday()),
    appointedOfficerId: new FormControl<string | null>(null, [Validators.required]),
    types: new FormControl<RelationshipType[]>([], { nonNullable: true }),
    cessationReason: new FormControl<ReasonOfCessation | null>(null, [Validators.required]),
    noticeOfResignation: new FormControl<string | null>(null, [Validators.required]),
    documentationType: new FormControl<DocumentationType>(DocumentationType.FormOnly, [Validators.required]),
  });

  override stepperForm = new FormGroup({
    [BulkCeaseCompanyOfficeholderStepsEnum.FormDescription]: new FormGroup({}),
    [BulkCeaseCompanyOfficeholderStepsEnum.CeaseCompanyOfficeholder]: this.form,
    [BulkCeaseCompanyOfficeholderStepsEnum.Summary]: new FormGroup({}),
  });

  constructor() {
    super();
    this.setupSteps(BulkCeaseCompanyOfficeholderStepsEnum);
  }

  ngOnInit(): void {
    this.minDate = DatepickerHelper.getDefaultMinDateStruct();
    this.maxDate = DatepickerHelper.getTodayStruct();

    this.listenReasonOfCessationChange();
    this.listenOfficerTypesChange();
    this.listenChangeDateChange(this.form.controls.changeDate, getForm484LateFeeNotification);

    CheckboxGroupComponent.addRequiredAtLeastOneValidator(this.form.controls.types);
    this.loadCompanies();
  }

  loadCompanies(): void {
    this.isLoading.set(true);

    const selectedEntitiesLoadRequests = this.companyRecords
      .map((companyRecord) => this.loadCompany(companyRecord.entityId));

    forkJoin(selectedEntitiesLoadRequests)
      .subscribe({
        next: (fullCompanies) => {
          this.companies = fullCompanies.filter(Boolean) as Company[];
          this.fillRows(this.companies);
          this.fillOfficeholdersOptions();
          this.isLoading.set(false);
        }
      });
  }

  onStepChange(stepIndex: BulkCeaseCompanyOfficeholderStepsEnum): void {
    if (stepIndex === BulkCeaseCompanyOfficeholderStepsEnum.Summary) {
      this.fillRowsErrors();
    }
  }

  override afterSubmit(changes: EntityChangeData[]) {
    this.setupChange(changes[0] as CompanyChangeOfficer);
  }

  override setupChange(change: CompanyChangeOfficer = this.formModel) {
  }

  override buildDocument(): Document[] | null {
    if (this.areAllRowsHaveErrors) return null;

    const changeDate = this.form.controls.changeDate.value!;
    const allowedTypes: Partial<Record<RelationshipType, boolean>> = this.form.controls.types.value
      .reduce((allowedType, relationshipType) => {
        allowedType[relationshipType] = true;
        return allowedType;
      }, {});
    const documentationType = this.form.controls.documentationType.value!;

    return this.companies
      .map((company, index) => {
        const relatedRow = this.rows[index];
        const changeAuthorisation = this.getChangeAuthorisation(company, documentationType, relatedRow, changeDate);
        changeAuthorisation.documentationType = DocumentationType.FormOnly;

        if (relatedRow.error) {
          return null;
        }

        const offices = company.currentOfficers
          .filter((relationship) => relationship.individualId === this.form.controls.appointedOfficerId.value && allowedTypes[relationship.type])
          .map((relationship) => {
            relationship.end = changeDate;
            return relationship;
          });

        const noticeOfResignation = this.form.controls.cessationReason.value == ReasonOfCessation.Resignation
          ? this.getNoticeOfResignationText(company, offices[0])
          : null;

        if (offices.length == 0) return null;

        const change = new CompanyChangeOfficer({
          ...this.form.value,
          actionType: CompanyChangeOfficerType.Removal,
          changeDate: changeDate,
          noticeOfResignation,
          offices
        });

        try {
          return new Document({
            changes: [change],
            changeAuthorisation,
            entityId: company.entityId,
            type: 'c:484',
            documentId: this.companyChangeData?.documentId,
            documentationType: this.form.controls.documentationType.value ?? 0
          });
        } catch (error) {
          return null;
        }
      })
      .filter(Boolean) as Document[];
  }

  override updateDocumentationTypeOptions(): void {
    this.documentTypeOptions = this.documentTypeOptions
      .map((option) => ({ ...option, disabled: option.value !== DocumentationType.FormOnly }));
    this.form.controls.documentationType.setValue(this.documentTypeOptions.find((option) => !option.disabled)?.value as DocumentationType ?? null);
  }

  onOfficeholderChange(value: unknown): void {
    if (!value) return;
    const individualId = value as string;
    this.form.controls.cessationReason.setValue(null);
    const appointedOfficerTypes = this.commonOfficeholdersAvailablePositions[individualId];
    this.form.controls.types.setValue(appointedOfficerTypes);
    this.commonOfficeholdersAvailablePositionsOptions = this.getRelationshipTypeOptions(appointedOfficerTypes);
  }

  listenOfficerTypesChange(): void {
    combineLatest([
      this.form.controls.types.valueChanges,
      this.form.controls.changeDate.valueChanges.pipe(startWith(this.form.controls.changeDate.value)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.form.controls.noticeOfResignation.setValue(this.getNoticeOfResignationText()));
  }

  listenReasonOfCessationChange(): void {
    this.form.controls.cessationReason.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reasonOfCessation) => {
        setControlDisabled(this.form.controls.noticeOfResignation, reasonOfCessation !== ReasonOfCessation.Resignation);
        setControlDisabled(this.form.controls.types, reasonOfCessation === ReasonOfCessation.Death);

        if (reasonOfCessation === ReasonOfCessation.Resignation) {
          this.form.controls.noticeOfResignation.setValue(this.getNoticeOfResignationText());
        }
      });
  }

  getNoticeOfResignationText(company: Company = this.companies[0], officer?: Relationship): string | null {
    if (!company) return '';

    const allowedToFillNoticeOfResignation =
      this.form.controls.cessationReason.value === ReasonOfCessation.Resignation
      && this.form.controls.appointedOfficerId.value !== null
      && this.form.controls.cessationReason.value !== null
      && this.form.controls.changeDate.value !== null;

    if (!allowedToFillNoticeOfResignation)
      return null;

    const relationship = officer ?? company.currentOfficers.find((relationship) => relationship.individualId == this.form.controls.appointedOfficerId.value);
    const selectedTypes = this.form.controls.types.value;

    const officerName = relationship?.individualDataOverride?.fullName ?? '';
    const officerAddress = relationship?.individualDataOverride?.address?.normalizedFullAddress ?? '';
    const positions = selectedTypes.length ? ' as ' + selectedTypes.map(type => RelationshipTypeLabels[type]).join(', ') : '';
    const nameOfCompanyAndACN = company?.name + (company?.entityNumber ? ` - ${ ACNPipe.formatValue(company?.entityNumber) }` : '');
    const resignationDate = DatepickerHelper.toString(this.form.controls.changeDate.value as Date);

    return `I, ${ officerName } of ${ officerAddress.trim() } hereby resign${ positions } of ${ nameOfCompanyAndACN } with effect from ${ resignationDate }`;
  }

  fillOfficeholdersOptions(companies: Company[] = this.companies): void {
    const companiesQuantity = companies.length;
    const companiesIndividualsCount: Record<string, number> = {};
    const individuals: Record<string, IndividualData> = {};
    const allIndividualsPositions: Record<string, Partial<Record<RelationshipType, number>>> = {};

    companies.forEach((company) => {
      const markedCompanyIndividualIds = new Set<string>();

      company.currentOfficers
        .filter((relationship) => relationship.isPosted)
        .forEach((relationship) => {
          const individualId = relationship.individualId!;

          if (!markedCompanyIndividualIds.has(individualId)) {
            if (companiesIndividualsCount[individualId]) {
              companiesIndividualsCount[individualId]++;
            } else {
              companiesIndividualsCount[individualId] = 1;

              if (relationship.individualDataOverride)
                individuals[individualId] = relationship.individualDataOverride;
            }
          }

          if (allIndividualsPositions[individualId]) {
            if (allIndividualsPositions[individualId][relationship.type]) {
              allIndividualsPositions[individualId][relationship.type]!++;
            } else {
              allIndividualsPositions[individualId][relationship.type] = 1;
            }
          } else {
            allIndividualsPositions[individualId] = {} as Record<(RelationshipType.AlternativeDirector | RelationshipType.Director | RelationshipType.Secretary), number>;
            allIndividualsPositions[individualId][relationship.type] = 1;
          }

          markedCompanyIndividualIds.add(individualId);
        });
    });

    const commonIds = Object.entries(companiesIndividualsCount)
      .filter(([individualId, count]) => count == companiesQuantity)
      .map(([individualId]) => individualId);

    this.commonOfficeholdersSelectOptions = commonIds.map((individualId) => ({
      label: IndividualData.buildFullNameWithMiddleLabel(individuals[individualId]),
      value: individualId,
    }));

    this.commonOfficeholdersAvailablePositions = Object.entries(allIndividualsPositions)
      .reduce((collection, [individualId, positionsCount]) => {
        collection[individualId] = Object.entries(positionsCount)
          // .filter(([_, count]) => count == companiesQuantity)
          .map(([typeStr]) => +typeStr as RelationshipType);

        return collection;
      }, {} as Record<string, RelationshipType[]>);
  }

  getRelationshipTypeOptions(types: RelationshipType[]): SelectOption<RelationshipType>[] {
    return types.map((type) => ({ label: RelationshipTypeLabels[type], value: type }));
  }

  // ===== ===== ===== Handle "Change is applied for" section and it's grid ===== ===== =====

  override getRowError(row: IBulkAppliedChange, rowIndex: number): string {
    const company = this.companies[rowIndex];
    const selectedIndividualId = this.form.controls.appointedOfficerId.value ?? null;
    const selectedTypes = this.form.controls.types.value;
    const isDirectorCeased = selectedTypes.includes(RelationshipType.Director);

    const selectedIndividualRelationships = company.currentOfficers.filter((relationship) => relationship.individualId == selectedIndividualId);
    const directors = company.currentOfficers.filter((relationship) => relationship.type == RelationshipType.Director);
    const fullName = company.currentOfficers.find(el => el.individualId === selectedIndividualId)?.individualDataOverride?.fullName ?? '';

    if (fullName && isDirectorCeased && directors.length == 1 && selectedIndividualRelationships.find((relationship) => relationship.type == RelationshipType.Director)) {
      // If there is a cease of the only director of the company
      return `You cannot cease the sole Director of the company ${ company.name }. Please appoint a new director before proceeding with this cessation.`;
    }

    const changeDate = this.form.controls.changeDate.value!;
    const isChangeDateEarlierOfficerAppointmentDate = selectedIndividualRelationships.some((relationship) => new Date(relationship.start!) > changeDate);

    if (isChangeDateEarlierOfficerAppointmentDate) {
      // Some relationships appointment date are later than the date of ceaseation
      return `Date of cease cannot be earlier or same as the appointment date`;
    }

    return super.getRowError(row, rowIndex);
  }

  override getRowWarning(row: IBulkAppliedChange, rowIndex: number): string {
    const selectedIndividualId = this.form.controls.appointedOfficerId.value ?? null;
    const selectedTypes = this.form.controls.types.value;
    const company = this.companies[rowIndex];
    const selectedIndividualRelationships = company.currentOfficers.filter((relationship) => relationship.individualId == selectedIndividualId);
    const fullName = company.currentOfficers.find(el => el.individualId === selectedIndividualId)?.individualDataOverride?.fullName ?? '';
    const selectedIndividualRelationshipTypes = selectedIndividualRelationships.map((relationship) => relationship.type);
    const areTypesCorresponds = selectedIndividualRelationshipTypes.length >= selectedTypes.length && selectedTypes.every((type) => selectedIndividualRelationshipTypes.includes(type));

    if (fullName && !areTypesCorresponds) {
      // Some roles of selected officer are not present in the company
      let presentTypesLabel = '';

      if (selectedIndividualRelationshipTypes.length == 1) {
        // Single type
        presentTypesLabel = `a ${ RelationshipTypeLabels[selectedIndividualRelationshipTypes[0]] }`;
      } else {
        // Multiple types
        presentTypesLabel = `${ selectedIndividualRelationshipTypes.map((type) => RelationshipTypeLabels[type]).join(', ') }`;
      }

      return `The current officeholder holds the position(s) of ${ presentTypesLabel } at ${ company.name }. A form will only be generated for positions that currently exist.`;
    }

    return super.getRowWarning(row, rowIndex);
  }

  get noticeOfResignationHidden(): boolean {
    return this.form.controls.cessationReason.value !== ReasonOfCessation.Resignation || this.companies?.length > 1;
  }
}
